import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, ShieldOff, Pencil, Repeat, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { todayISO, UNAVAIL_COLORS, unavailColor } from "@/lib/utils-app";

/**
 * V1.38 · Diálogo de gestión de indisponibilidad del profesor.
 * Reutiliza el endpoint `/api/teacher-unavailability` (GET, POST, DELETE).
 */
export default function TeacherUnavailabilityDialog({ open, onClose, teacher, initialDate, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    title: "",
    date: initialDate || todayISO(),
    time: "09:00",
    end_time: "10:00",
    notes: "",
    // V1.43 · Vacaciones/ausencia — bloqueo por días completos con rango.
    all_day: false,
    date_to: "",
    // V1.69 · Color pastel del bloqueo en el calendario.
    color: "gris",
  });
  const [saving, setSaving] = useState(false);
  // V1.61 · Repetición semanal (ej. comidas L-V). ISO: 0=lunes…6=domingo.
  const [repeat, setRepeat] = useState({ enabled: false, weekdays: [], until: "" });
  // V1.61 · Edición de bloqueos existentes (constitución: todo modificable).
  const [editId, setEditId] = useState(null);
  const [editSeriesId, setEditSeriesId] = useState("");
  const WEEKDAYS = [["L", 0], ["M", 1], ["X", 2], ["J", 3], ["V", 4], ["S", 5], ["D", 6]];
  // V1.42 · Listado de motivos desde Configuración → Listado de valores.
  const [reasons, setReasons] = useState([]);
  const [reasonKey, setReasonKey] = useState(""); // valor del select
  const OTROS = "Otros";

  const load = async () => {
    if (!teacher) return;
    try {
      const { data } = await api.get("/teacher-unavailability", { params: { teacher, date_from: todayISO() } });
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (_) {
      setRows([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, date: initialDate || todayISO(), title: "", notes: "", all_day: false, date_to: "", color: "gris" }));
    setReasonKey("");
    setRepeat({ enabled: false, weekdays: [], until: "" });
    setEditId(null);
    // Cargamos el listado de motivos desde Configuración (fuente única).
    api.get("/settings/lookup/teacher_unavailability_reasons")
      .then(({ data }) => {
        const list = Array.isArray(data?.values) ? data.values : [];
        setReasons(list);
        if (list.length > 0) {
          const first = list[0];
          setReasonKey(first);
          if (first !== OTROS) setForm((f) => ({ ...f, title: first }));
        }
      })
      .catch(() => setReasons([]));
    load();
  }, [open, teacher, initialDate]);

  const submit = async (e, scope = "one") => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Título obligatorio"); return; }
    if (!teacher) { toast.error("Profesor no seleccionado"); return; }
    // V1.43 · En modo "día completo" saltamos la validación de horas y
    // exigimos que la fecha fin (si se informa) sea >= fecha inicio.
    if (form.all_day) {
      if (form.date_to && form.date_to < form.date) {
        toast.error("La fecha fin debe ser posterior o igual a la fecha inicio.");
        return;
      }
    } else {
      // V1.39 fix (code review MEDIUM) · Franja válida: fin > inicio.
      const [h1, m1] = String(form.time || "").split(":").map(Number);
      const [h2, m2] = String(form.end_time || "").split(":").map(Number);
      const s = Number.isFinite(h1) ? h1 * 60 + (m1 || 0) : -1;
      const e2 = Number.isFinite(h2) ? h2 * 60 + (m2 || 0) : -1;
      if (s < 0 || e2 < 0) { toast.error("Hora inválida (HH:MM)."); return; }
      if (e2 <= s) { toast.error("La hora de fin debe ser posterior al inicio."); return; }
    }
    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.put(`/teacher-unavailability/${editId}`, {
          title: form.title, date: form.date, time: form.time,
          end_time: form.end_time, notes: form.notes, all_day: form.all_day,
          color: form.color || "gris",
        }, { params: { scope } });
        toast.success(scope === "series"
          ? `Serie modificada (${data?.updated ?? 0} días actualizados).`
          : "Bloqueo modificado.");
        setEditId(null);
        setEditSeriesId("");
      } else {
        if (repeat.enabled && (!repeat.until || repeat.weekdays.length === 0)) {
          toast.error("Repetición: elige los días de la semana y la fecha 'hasta'.");
          setSaving(false);
          return;
        }
        const { data } = await api.post("/teacher-unavailability", {
          teacher, ...form,
          repeat_weekdays: repeat.enabled ? repeat.weekdays : [],
          repeat_until: repeat.enabled ? repeat.until : "",
        });
        toast.success(repeat.enabled
          ? `${data?.created ?? ""} bloqueos repetidos registrados`
          : form.all_day && form.date_to ? "Bloqueo de vacaciones registrado" : "Bloqueo registrado");
      }
      setForm((f) => ({ ...f, title: "", notes: "", date_to: "" }));
      setRepeat({ enabled: false, weekdays: [], until: "" });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setEditSeriesId(r.series_id || "");
    setForm({
      title: r.title || "", date: r.date, time: r.time || "09:00",
      end_time: r.end_time || "10:00", notes: r.notes || "",
      all_day: !!r.all_day, date_to: "", color: r.color || "gris",
    });
    setReasonKey(reasons.includes(r.title) ? r.title : OTROS);
    setRepeat({ enabled: false, weekdays: [], until: "" });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm((f) => ({ ...f, title: "", notes: "", all_day: false, date_to: "", color: "gris" }));
    setReasonKey(reasons[0] || "");
  };

  const removeRow = async (id) => {
    try {
      await api.delete(`/teacher-unavailability/${id}`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="teacher-unavailability-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-4 w-4 text-red-600" /> Marcar No disponible — profesor {teacher || "—"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Motivo *</label>
            <select
              value={reasonKey}
              onChange={(e) => {
                const v = e.target.value;
                setReasonKey(v);
                setForm((f) => ({ ...f, title: v === OTROS ? "" : v }));
              }}
              data-testid="unavail-reason-select"
              className="w-full rounded-lg border border-neutral-300 px-3 h-10 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {reasons.length === 0 && <option value="">— Sin motivos configurados —</option>}
              {reasons.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {reasonKey === OTROS && (
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Especifica el motivo…"
                data-testid="unavail-title"
                className="mt-2 w-full rounded-lg border border-neutral-300 px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            )}
            <p className="text-[10px] text-neutral-500 mt-1">
              Configura los motivos en Configuración → Listado de valores.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700 mb-1 block">
                {form.all_day ? "Desde *" : "Fecha *"}
              </label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="unavail-date"
                className="w-full rounded-lg border border-neutral-300 px-2 h-10 text-sm" />
            </div>
            {form.all_day ? (
              <div className="col-span-2">
                <label className="text-xs font-semibold text-neutral-700 mb-1 block">Hasta (incl.)</label>
                <input
                  type="date"
                  value={form.date_to || ""}
                  min={form.date}
                  onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                  data-testid="unavail-date-to"
                  placeholder="Mismo día"
                  className="w-full rounded-lg border border-neutral-300 px-2 h-10 text-sm"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Inicio *</label>
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid="unavail-time"
                    className="w-full rounded-lg border border-neutral-300 px-2 h-10 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Fin *</label>
                  <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    data-testid="unavail-endtime"
                    className="w-full rounded-lg border border-neutral-300 px-2 h-10 text-sm" />
                </div>
              </>
            )}
          </div>
          {/* V1.43 · Vacaciones / ausencia — bloqueo por días completos. */}
          <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.all_day}
              onChange={(e) => setForm({ ...form, all_day: e.target.checked, date_to: e.target.checked ? form.date_to : "" })}
              data-testid="unavail-all-day"
              className="h-4 w-4 accent-orange-500"
            />
            Día completo (vacaciones / ausencia)
            <span className="text-[10px] font-normal text-neutral-500 ml-1">
              — pinta los días enteros en gris y bloquea cualquier sesión.
            </span>
          </label>
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Color en el calendario</label>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="unavail-color-picker">
              {UNAVAIL_COLORS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  title={c.label}
                  onClick={() => setForm({ ...form, color: c.key })}
                  data-testid={`unavail-color-${c.key}`}
                  className={"h-8 w-8 rounded-lg transition-transform " + c.swatch +
                    ((form.color || "gris") === c.key ? " ring-2 ring-offset-1 ring-neutral-800 scale-105" : " opacity-80 hover:opacity-100")}
                />
              ))}
              <span className={"ml-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold " + unavailColor(form.color).chip} data-testid="unavail-color-preview">
                <span className={"inline-block h-1.5 w-1.5 rounded-full " + unavailColor(form.color).dot} />
                {form.title || "Vista previa"}
              </span>
            </div>
            <p className="text-[10px] text-neutral-500 mt-1">
              Gris es el formato del día completo; usa un pastel para comidas, descansos u otros huecos.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Observaciones</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Sólo visible para el profesor propietario y administración."
              data-testid="unavail-notes"
              className="w-full rounded-lg border border-neutral-300 px-3 py-1.5 text-sm resize-none" />
          </div>
          {/* V1.61 · Repetición semanal (solo al crear) */}
          {!editId && !form.all_day && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-2.5 space-y-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700 select-none cursor-pointer">
                <input type="checkbox" checked={repeat.enabled}
                  onChange={(e) => setRepeat((r) => ({ ...r, enabled: e.target.checked }))}
                  data-testid="unavail-repeat-toggle" className="h-4 w-4 accent-orange-500" />
                <Repeat className="h-3.5 w-3.5" /> Repetir semanalmente
                <span className="text-[10px] font-normal text-neutral-500">— ej. comidas de lunes a viernes</span>
              </label>
              {repeat.enabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1 flex-wrap">
                    {WEEKDAYS.map(([label, v]) => {
                      const on = repeat.weekdays.includes(v);
                      return (
                        <button key={v} type="button"
                          onClick={() => setRepeat((r) => ({ ...r, weekdays: on ? r.weekdays.filter((d) => d !== v) : [...r.weekdays, v] }))}
                          className={"h-8 w-8 rounded-lg text-xs font-bold transition-colors " + (on ? "bg-orange-500 text-white" : "bg-white border border-neutral-300 text-neutral-600 hover:bg-neutral-100")}
                          data-testid={`unavail-repeat-day-${v}`}>
                          {label}
                        </button>
                      );
                    })}
                    <button type="button"
                      onClick={() => setRepeat((r) => ({ ...r, weekdays: [0, 1, 2, 3, 4] }))}
                      className="h-8 px-2.5 rounded-lg text-[11px] font-bold border border-orange-300 text-orange-700 hover:bg-orange-50"
                      data-testid="unavail-repeat-lv">
                      L–V
                    </button>
                  </div>
                  <label className="text-xs font-semibold text-neutral-700 flex items-center gap-2">
                    Hasta (incl.)
                    <input type="date" value={repeat.until} min={form.date}
                      onChange={(e) => setRepeat((r) => ({ ...r, until: e.target.value }))}
                      data-testid="unavail-repeat-until"
                      className="rounded-lg border border-neutral-300 px-2 h-9 text-sm font-normal" />
                  </label>
                </div>
              )}
            </div>
          )}
          <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="unavail-save">
            {saving ? "Guardando…" : editId ? "Guardar cambios" : "Registrar bloqueo"}
          </button>
          {editId && editSeriesId && (
            <button type="button" disabled={saving} onClick={(e) => submit(e, "series")}
              title="Aplica motivo, franja y color a todos los días de esta repetición"
              className="w-full !h-9 rounded-lg bg-neutral-800 hover:bg-neutral-900 text-white text-xs font-bold disabled:opacity-50"
              data-testid="unavail-save-series">
              Guardar para toda la serie
            </button>
          )}
          {editId && (
            <button type="button" onClick={cancelEdit} className="btn-secondary w-full !h-9" data-testid="unavail-cancel-edit">
              <XIcon className="h-3.5 w-3.5" /> Cancelar edición
            </button>
          )}
        </form>

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600 mb-1.5">
            Bloqueos futuros ({rows.length})
          </div>
          {rows.length === 0 ? (
            <div className="text-xs text-neutral-500 py-2">Sin bloqueos programados.</div>
          ) : (
            <div className="space-y-1.5" data-testid="unavail-list">
              {rows.map((r) => (
                <div key={r.id} data-testid={`unavail-row-${r.id}`} className={"flex items-center gap-2 text-xs rounded-lg border px-2.5 py-1.5 " + (editId === r.id ? "border-orange-400 bg-orange-50" : "border-red-200 bg-red-50")}>
                  <span className={"inline-block h-3 w-3 rounded shrink-0 " + unavailColor(r.color).swatch} title={unavailColor(r.color).label} />
                  <span className="font-mono tabular-nums text-neutral-700">{r.date}</span>
                  {r.all_day ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide rounded bg-neutral-700 text-white px-1.5 py-0.5">Día completo</span>
                  ) : (
                    <span className="font-mono tabular-nums text-neutral-500">{r.time}–{r.end_time}</span>
                  )}
                  <span className="flex-1 truncate font-semibold text-red-900">{r.title}</span>
                  <button type="button" onClick={() => startEdit(r)} data-testid={`unavail-edit-${r.id}`}
                    className="text-neutral-500 hover:text-orange-600" title="Modificar bloqueo">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => removeRow(r.id)} data-testid={`unavail-delete-${r.id}`}
                    className="text-red-700 hover:text-red-900" title="Eliminar bloqueo">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="unavail-close">
            Cerrar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
